import {
  ImageIcon,
  InfoIcon,
  LightbulbIcon,
  TriangleAlertIcon,
} from 'lucide-react'

import { Markdown } from '@/components/ui/Markdown'
import { HELP_BOT_URL } from '@/config'
import { cn } from '@/utils'
import { helpMarkdownComponents } from './markdownComponents'

type CalloutTone = 'note' | 'tip' | 'warning'

type Block =
  | { id: string; kind: 'markdown'; text: string }
  | { id: string; kind: 'callout'; tone: CalloutTone; text: string }
  | { id: string; kind: 'mock'; caption: string; alt: string; image: string }

const CALLOUT_TONES: Record<string, CalloutTone> = {
  note: 'note',
  tip: 'tip',
  warning: 'warning',
}

// Splits an article body into plain markdown, callout boxes (`:::note` / `:::tip`
// / `:::warning`), and UI-mock placeholders (`:::ui scene=… / caption: / alt:`).
// The mock scene kit is not ported; the app shows the caption and alt text.
function parseArticleBlocks(body: string): Block[] {
  const blocks: Block[] = []
  const lines = body.split('\n')
  let markdown: string[] = []
  const nextId = () => `block-${blocks.length}`

  const flushMarkdown = () => {
    const text = markdown.join('\n').trim()
    if (text) blocks.push({ id: nextId(), kind: 'markdown', text })
    markdown = []
  }

  for (let index = 0; index < lines.length; index += 1) {
    const opener = lines[index].trimStart()
    if (!opener.startsWith(':::')) {
      markdown.push(lines[index])
      continue
    }

    flushMarkdown()
    const directive = opener.slice(3).trim()
    const inner: string[] = []
    index += 1
    while (index < lines.length && lines[index].trimStart() !== ':::') {
      inner.push(lines[index])
      index += 1
    }

    if (directive.startsWith('ui')) {
      blocks.push({
        id: nextId(),
        kind: 'mock',
        caption: fieldValue(inner, 'caption'),
        alt: fieldValue(inner, 'alt'),
        image: fieldValue(inner, 'image'),
      })
    } else {
      const tone = CALLOUT_TONES[directive.split(/\s+/)[0]] ?? 'note'
      blocks.push({
        id: nextId(),
        kind: 'callout',
        tone,
        text: inner.join('\n').trim(),
      })
    }
  }

  flushMarkdown()
  return blocks
}

function fieldValue(lines: string[], key: string): string {
  const match = lines.find((line) => line.trimStart().startsWith(`${key}:`))
  return match ? match.slice(match.indexOf(':') + 1).trim() : ''
}

const CALLOUT_STYLES: Record<
  CalloutTone,
  { box: string; icon: string; Icon: typeof InfoIcon }
> = {
  note: { box: 'border-blue-3 bg-blue-1', icon: 'text-blue-5', Icon: InfoIcon },
  tip: {
    box: 'border-brand/30 bg-brand/5',
    icon: 'text-brand',
    Icon: LightbulbIcon,
  },
  warning: {
    box: 'border-amber-300 bg-amber-50',
    icon: 'text-amber-600',
    Icon: TriangleAlertIcon,
  },
}

interface ArticleMarkdownProps {
  body: string
  onOpenArticle: (slug: string) => void
}

// Mock images are served from the same help-center origin as the article body,
// so a root-relative `src` gets prefixed with that origin; an absolute one is
// used as-is.
function mockImageUrl(image: string): string {
  return image.startsWith('http') ? image : `${HELP_BOT_URL}${image}`
}

export function ArticleMarkdown({ body, onOpenArticle }: ArticleMarkdownProps) {
  const linkComponents = helpMarkdownComponents(onOpenArticle)
  const blocks = parseArticleBlocks(body)

  return (
    <div className="space-y-4">
      {blocks.map((block) => {
        if (block.kind === 'markdown') {
          return (
            <Markdown
              key={block.id}
              content={block.text}
              componentsOverride={linkComponents}
            />
          )
        }
        if (block.kind === 'callout') {
          const { box, icon, Icon } = CALLOUT_STYLES[block.tone]
          return (
            <div
              key={block.id}
              className={cn('flex gap-3 rounded-xl border p-4', box)}
            >
              <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', icon)} />
              <div className="[&_p:first-child]:mt-0 text-sm">
                <Markdown
                  content={block.text}
                  componentsOverride={linkComponents}
                />
              </div>
            </div>
          )
        }
        if (block.image) {
          return (
            <figure key={block.id} className="space-y-2">
              <img
                src={mockImageUrl(block.image)}
                alt={block.alt}
                loading="lazy"
                className="w-full rounded-xl border border-border"
              />
              {block.caption && (
                <figcaption className="text-sm text-blue-5 text-pretty">
                  {block.caption}
                </figcaption>
              )}
            </figure>
          )
        }
        return (
          <figure
            key={block.id}
            className="flex items-center gap-3 rounded-xl border border-dashed border-blue-3 bg-blue-1/60 p-4"
          >
            <ImageIcon className="h-5 w-5 shrink-0 text-blue-4" />
            <figcaption className="text-sm text-blue-5 text-pretty">
              {block.caption || block.alt}
            </figcaption>
          </figure>
        )
      })}
    </div>
  )
}
